import { useEditor } from '@craftjs/core';
import { PaddingMarginFields } from '../PaddingMarginFields/PaddingMarginFields';
import type { TRBLNumberCOORDS } from '@builder4/shared/types/layout';
import type { FormProps } from '@builder4/shared/components/Form';
import { useInspectorNode } from '@/hooks/useInspectorNode';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { FORM_NODE_NAME } from '@/utils/constants';

type FormFieldLayoutProps = {
  fieldMargin?: TRBLNumberCOORDS;
  fieldInputPadding?: TRBLNumberCOORDS;
  fieldBorderRadius?: TRBLNumberCOORDS;
};

type PMValues = { all: number; top: number; right: number; bottom: number; left: number };

const NodeFieldLayout = () => {
  const { actions } = useEditor();
  const { id, props: nodeProps, node } = useInspectorNode<Partial<FormProps>>();

  const { getBPProp, setBPProp } = useBreakpointAwareProp(node?.data.displayName || FORM_NODE_NAME);

  // Read props using breakpoint-aware getter
  const fieldMargin = getBPProp(nodeProps, 'fieldMargin');
  const fieldInputPadding = getBPProp(nodeProps, 'fieldInputPadding');
  const fieldBorderRadius = getBPProp(nodeProps, 'fieldBorderRadius');

  const convertToTRBL = (values: PMValues): TRBLNumberCOORDS => {
    return [values.top, values.right, values.bottom, values.left];
  };

  const convertFromTRBL = (trbl?: TRBLNumberCOORDS): PMValues => {
    if (!trbl) return { all: 0, top: 0, right: 0, bottom: 0, left: 0 };
    const [top, right, bottom, left] = trbl;
    const allSame = top === right && right === bottom && bottom === left;
    return { all: allSame ? top : 0, top, right, bottom, left };
  };

  const calculateVariant = (value: PMValues): 'locked' | 'unlocked' => {
    let hasSame = !!value.all;
    if (!hasSame) {
      // Check if all individual values are equal
      hasSame =
        value.top === value.right && value.right === value.bottom && value.bottom === value.left;
    }
    return hasSame ? 'locked' : 'unlocked';
  };

  const handleMarginChange = (values: PMValues, skipHistory = false) => {
    const newMargin = convertToTRBL(values);
    if (skipHistory) {
      actions.history.ignore().setProp(id, (props: Partial<FormFieldLayoutProps>) => {
        setBPProp(props, 'fieldMargin', newMargin, FORM_NODE_NAME);
      });
    } else {
      actions.setProp(id, (props: Partial<FormFieldLayoutProps>) => {
        setBPProp(props, 'fieldMargin', newMargin, FORM_NODE_NAME);
      });
    }
  };

  const handleMarginChangeComplete = (values: PMValues, initialValues: PMValues) => {
    const initialMargin = convertToTRBL(initialValues);
    const newMargin = convertToTRBL(values);
    actions.history.ignore().setProp(id, (props: Partial<FormFieldLayoutProps>) => {
      setBPProp(props, 'fieldMargin', initialMargin, FORM_NODE_NAME);
    });
    actions.setProp(id, (props: Partial<FormFieldLayoutProps>) => {
      setBPProp(props, 'fieldMargin', newMargin, FORM_NODE_NAME);
    });
  };

  const handlePaddingChange = (values: PMValues, skipHistory = false) => {
    const newPadding = convertToTRBL(values);
    if (skipHistory) {
      actions.history.ignore().setProp(id, (props: Partial<FormFieldLayoutProps>) => {
        setBPProp(props, 'fieldInputPadding', newPadding, FORM_NODE_NAME);
      });
    } else {
      actions.setProp(id, (props: Partial<FormFieldLayoutProps>) => {
        setBPProp(props, 'fieldInputPadding', newPadding, FORM_NODE_NAME);
      });
    }
  };

  const handlePaddingChangeComplete = (values: PMValues, initialValues: PMValues) => {
    const initialPadding = convertToTRBL(initialValues);
    const newPadding = convertToTRBL(values);
    actions.history.ignore().setProp(id, (props: Partial<FormFieldLayoutProps>) => {
      setBPProp(props, 'fieldInputPadding', initialPadding, FORM_NODE_NAME);
    });
    actions.setProp(id, (props: Partial<FormFieldLayoutProps>) => {
      setBPProp(props, 'fieldInputPadding', newPadding, FORM_NODE_NAME);
    });
  };

  const handleCornersChange = (values: PMValues, skipHistory = false) => {
    const newCorners = convertToTRBL(values);
    if (skipHistory) {
      actions.history.ignore().setProp(id, (props: Partial<FormFieldLayoutProps>) => {
        setBPProp(props, 'fieldBorderRadius', newCorners, FORM_NODE_NAME);
      });
    } else {
      actions.setProp(id, (props: Partial<FormFieldLayoutProps>) => {
        setBPProp(props, 'fieldBorderRadius', newCorners, FORM_NODE_NAME);
      });
    }
  };

  const handleCornersChangeComplete = (values: PMValues, initialValues: PMValues) => {
    const initialCorners = convertToTRBL(initialValues);
    const newCorners = convertToTRBL(values);
    actions.history.ignore().setProp(id, (props: Partial<FormFieldLayoutProps>) => {
      setBPProp(props, 'fieldBorderRadius', initialCorners, FORM_NODE_NAME);
    });
    actions.setProp(id, (props: Partial<FormFieldLayoutProps>) => {
      setBPProp(props, 'fieldBorderRadius', newCorners, FORM_NODE_NAME);
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs font-semibold text-neutral-900">Layout</div>
      <PaddingMarginFields
        className="w-full max-w-[216px]"
        label="Margin"
        value={convertFromTRBL(fieldMargin)}
        onChange={(v) => handleMarginChange(v, true)}
        onChangeComplete={handleMarginChangeComplete}
        variant={calculateVariant(convertFromTRBL(fieldMargin))}
      />
      <PaddingMarginFields
        className="w-full max-w-[216px]"
        label="Padding"
        value={convertFromTRBL(fieldInputPadding)}
        onChange={(v) => handlePaddingChange(v, true)}
        onChangeComplete={handlePaddingChangeComplete}
        variant={calculateVariant(convertFromTRBL(fieldInputPadding))}
      />
      <PaddingMarginFields
        className="w-full max-w-[216px]"
        label="Corners"
        value={convertFromTRBL(fieldBorderRadius)}
        onChange={(v) => handleCornersChange(v, true)}
        onChangeComplete={handleCornersChangeComplete}
        variant={calculateVariant(convertFromTRBL(fieldBorderRadius))}
        min={0}
        max={100}
      />
    </div>
  );
};

export default NodeFieldLayout;
